/**
 * usePackJobs · custom hook globale per i job "Pacchetto Social" in
 * background.
 *
 * Pattern:
 *   - Stato persistito in localStorage (`pronto.pack_jobs`) → resiste al
 *     reload e alla navigazione tra pagine.
 *   - Polling ogni 4s per i soli job non-finali (queued/running).
 *   - Quando un job passa a "done", emette un toast "✓ Pacchetto pronto"
 *     e l'utente può scaricare dal tray flottante.
 *
 * Pubblica un singleton via Context, così la `<PackJobsTray />` montata in
 * App.js e il PackBox dentro a SocialStudioTab leggono la stessa lista.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { makeAssetFilename } from "@/lib/assetNaming";

const LS_KEY = "pronto.pack_jobs";
const POLL_INTERVAL_MS = 4000;

const PackJobsContext = createContext(null);

function _loadFromLS() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((j) => j && j.job_id && j.property_id);
  } catch {
    return [];
  }
}

function _saveToLS(jobs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(jobs));
  } catch {
    /* ignore quota / ssr */
  }
}

export function PackJobsProvider({ children }) {
  const [jobs, setJobs] = useState(() => _loadFromLS());
  const lastNotifiedRef = useRef(new Set());

  // Persisti su LS ad ogni cambiamento
  useEffect(() => {
    _saveToLS(jobs);
  }, [jobs]);

  // Polling ogni 4s sui soli job non-finali. Esclude i job optimistic
  // (job_id che inizia con "pending_") perché il loro reale job_id arriva
  // solo dopo che POST /pack/start risolve.
  const pollKey = jobs
    .filter(
      (j) =>
        (j.status === "queued" || j.status === "running") &&
        !j.job_id.startsWith("pending_"),
    )
    .map((j) => `${j.job_id}:${j.status}`)
    .sort()
    .join("|");
  useEffect(() => {
    if (!pollKey) return;
    let cancelled = false;
    const id = setInterval(async () => {
      // Snapshot locale dei pending per questo tick (evita race con setJobs)
      const pending = pollKey.split("|").map((k) => k.split(":")[0]);
      const updates = await Promise.all(
        pending.map(async (jid) => {
          const job = jobs.find((j) => j.job_id === jid);
          if (!job) return null;
          try {
            const { data } = await api.get(
              `/properties/${job.property_id}/social/pack/${jid}/status`,
            );
            return { job_id: jid, ...data };
          } catch (err) {
            const code = err?.response?.status;
            if (code === 404) return { job_id: jid, status: "expired" };
            return null;
          }
        }),
      );
      if (cancelled) return;
      setJobs((prev) =>
        prev.map((j) => {
          const u = updates.find((x) => x && x.job_id === j.job_id);
          return u ? { ...j, ...u } : j;
        }),
      );
      updates.forEach((u) => {
        if (!u) return;
        if (u.status === "done" && !lastNotifiedRef.current.has(u.job_id)) {
          lastNotifiedRef.current.add(u.job_id);
          const job = jobs.find((j) => j.job_id === u.job_id);
          toast.success("Pacchetto Social pronto · scarica dal vassoio in basso", {
            description: job?.property_title || job?.property_id,
            duration: 8000,
          });
        }
        if (u.status === "failed" && !lastNotifiedRef.current.has(u.job_id)) {
          lastNotifiedRef.current.add(u.job_id);
          toast.error("Generazione pacchetto fallita");
        }
      });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollKey]);

  const enqueue = useCallback(async (params) => {
    // Optimistic write: aggiungi SUBITO un job in stato 'queued' nel tray
    // (anche se il backend è lento). L'agente vede feedback istantaneo nel
    // tray flottante. Quando POST risolve, sostituisci il temp_id con il
    // vero job_id; se rifiuta, rimuovi.
    const tempId = `pending_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const optimistic = {
      job_id: tempId,
      property_id: params.property_id,
      property_title: params.property_title || "",
      status: "queued",
      started_at: new Date().toISOString(),
      params: params.body || {},
      _optimistic: true,
    };
    setJobs((prev) => [...prev, optimistic]);
    // Toast IMMEDIATO: indipendente dal tempo di risposta del backend
    toast.success("Pacchetto in lavorazione · ti avviso quando è pronto", {
      description:
        "Puoi continuare a lavorare. Riceverai una notifica e troverai il pacchetto pronto nel vassoio in basso a destra.",
      duration: 6000,
    });

    // Fire-and-forget: la POST risolve in background, l'agente intanto
    // naviga via senza bloccare. Errori gestiti internamente con rollback.
    (async () => {
      try {
        const { data } = await api.post(
          `/properties/${params.property_id}/social/pack/start`,
          params.body || {},
          { timeout: 60000 },
        );
        if (!data?.job_id) throw new Error("Job non avviato");
        setJobs((prev) =>
          prev.map((j) =>
            j.job_id === tempId
              ? {
                  ...j,
                  job_id: data.job_id,
                  status: data.status || "queued",
                  _optimistic: false,
                }
              : j,
          ),
        );
      } catch (err) {
        setJobs((prev) => prev.filter((j) => j.job_id !== tempId));
        toast.error(
          err?.response?.data?.detail || err?.message || "Avvio pacchetto fallito",
        );
      }
    })();

    return optimistic;
  }, []);

  const remove = useCallback((jobId) => {
    setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
    lastNotifiedRef.current.delete(jobId);
  }, []);

  const download = useCallback(
    async (job) => {
      try {
        const resp = await api.get(
          `/properties/${job.property_id}/social/pack/${job.job_id}/download`,
          { responseType: "blob" },
        );
        const blob = new Blob([resp.data], { type: "application/zip" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = makeAssetFilename({
          propertyTitle: job.property_title,
          kind: "pack",
          ext: "zip",
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
        toast.success("Download avviato");
        // Una volta scaricato, lo rimuoviamo dal tray
        remove(job.job_id);
      } catch (err) {
        const code = err?.response?.status;
        if (code === 410) {
          toast.error("Pacchetto scaduto · rilancialo dall'editor social");
          remove(job.job_id);
        } else if (code === 409) {
          toast.info("Il pacchetto non è ancora pronto");
        } else {
          toast.error("Download non riuscito");
        }
      }
    },
    [remove],
  );

  const value = useMemo(
    () => ({ jobs, enqueue, remove, download }),
    [jobs, enqueue, remove, download],
  );

  return <PackJobsContext.Provider value={value}>{children}</PackJobsContext.Provider>;
}

export function usePackJobs() {
  const ctx = useContext(PackJobsContext);
  if (!ctx) {
    throw new Error("usePackJobs deve essere usato dentro <PackJobsProvider>");
  }
  return ctx;
}
